// LAP-767 — Agrégation des sources Life du conseiller pro / formation.
//
// Même pattern que `fetchEcoAdvisorSources` : on isole chaque endpoint
// (catch indépendant) pour qu'un endpoint absent ne fasse pas tomber tout
// l'agrégat. `buildProAdvisor` produit alors un diagnostic partiel mais
// exploitable.
//
// Source principale : /api/Professional/getProfessionnelle/ qui renvoie un
// tableau de cartes typées (profilutilisateur, competences, softskills,
// tempsformation, tempstravail, opportunite, valeurhoraire, recommandation).
// On passe le tableau brut à buildProAdvisor qui démêle par `type`.
import { makeGetRequest } from 'utils/api'

/**
 * Normalise une réponse API en tableau, quelle que soit l'enveloppe.
 */
export function extractList(payload) {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.results)) return payload.results
  if (Array.isArray(payload.data)) return payload.data
  return []
}

/**
 * Récupère et démêle les sources nécessaires à `buildProAdvisor`.
 *
 * @param {object} fallbackUser - profil Redux à utiliser si /api/user/user/
 *   échoue.
 * @returns {Promise<object>} { user, cards, advice }
 */
export async function fetchProAdvisorSources(fallbackUser = {}) {
  const safeGet = async (path) => {
    try {
      const r = await makeGetRequest(path, {}, true)
      return r?.data ?? null
    } catch (_) {
      return null
    }
  }

  const [userData, professionnelle] = await Promise.all([
    safeGet('/api/user/user/'),
    safeGet('/api/Professional/getProfessionnelle/'),
  ])

  const cards = extractList(professionnelle)
  // La carte "recommandation" porte la dernière reco persistée (souvent vide
  // tant qu'aucune génération n'a eu lieu) — on la passe à part pour le panneau
  // dédié, comme la carte "conseils" côté écologie.
  const recommandationCard = cards.find((c) => c?.type === 'recommandation')

  return {
    user: userData || fallbackUser || {},
    cards,
    advice: recommandationCard?.data || {},
  }
}

/**
 * LAP-767 — Récupère l'historique réel des recommandations pro
 * (si le backend l'expose un jour, mirroir de l'écologie). Toléré vide.
 *
 * @returns {Promise<Array>} liste de recommandations (peut être vide).
 */
export async function fetchProAdviceHistory() {
  try {
    const r = await makeGetRequest(
      '/api/Professional/getProfessionnelle/?history=true',
      {},
      true,
    )
    return extractList(r?.data)
  } catch (_) {
    return []
  }
}
